import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeInUp, SectionLabel, AccentLine } from './AnimationWrappers';

const KNIT_FABRICS = [
  { level: 1, name: 'Cotton Single Jersey', gsm: '120-180', handling: 'Standard', desc: 'Basic circular knit. Consistent tension, minimal curl management. Entry-level complexity.' },
  { level: 2, name: 'Piqu\u00e9 / Interlock', gsm: '180-240', handling: 'Standard', desc: 'Double-knit structure. Requires balanced feed for dimensional stability.' },
  { level: 3, name: 'French Terry / Fleece', gsm: '240-350', handling: 'Moderate', desc: 'Loop-back construction. Napping control critical. Shrinkage management required.' },
  { level: 4, name: 'Rib Knit / Ponte', gsm: '200-320', handling: 'Moderate', desc: 'High elasticity. Pattern matching across seams. Controlled stretch during cutting.' },
  { level: 5, name: 'Spacer / 3D Mesh', gsm: '200-400', handling: 'High', desc: 'Three-dimensional knit. Specialized needle plates. Thermal bonding required at seams.' },
  { level: 6, name: 'Warp Knit / Tricot', gsm: '80-200', handling: 'High', desc: 'Directional stretch patterns. High-speed sewing with differential feed essential.' },
  { level: 7, name: 'Aramid / Kevlar Knits', gsm: '180-340', handling: 'Extreme', desc: 'Ballistic-grade fibers. Specialized cutting tools. Heat-resistant thread required.' },
];

const WOVEN_FABRICS = [
  { level: 1, name: 'Cotton Poplin / Chambray', gsm: '90-150', handling: 'Standard', desc: 'Plain weave. Consistent grain. Standard sewing parameters.' },
  { level: 2, name: 'Twill / Canvas', gsm: '180-320', handling: 'Standard', desc: 'Diagonal weave pattern. Heavier needle gauges. Consistent seam allowance critical.' },
  { level: 3, name: 'Ripstop Nylon', gsm: '40-120', handling: 'Moderate', desc: 'Grid reinforcement pattern. Low friction sewing. Precise heat-cutting for edge sealing.' },
  { level: 4, name: 'Taslan / Micro-fiber', gsm: '80-160', handling: 'Moderate', desc: 'Air-textured surface. Requires Teflon presser feet. Static control during cutting.' },
  { level: 5, name: 'Softshell / Bonded', gsm: '200-400', handling: 'High', desc: 'Multi-layer bonded construction. Delamination risk. Specialized bonding adhesives.' },
  { level: 6, name: 'Gore-Tex / eVent', gsm: '120-280', handling: 'High', desc: 'Membrane fabrics. Seam sealing mandatory. No pin holes tolerated.' },
  { level: 7, name: 'PTFE Membrane Composites', gsm: '150-350', handling: 'Extreme', desc: 'Chemical-resistant membranes. Cleanroom assembly. Ultrasonic welding required.' },
];

const getBarColor = (level) => {
  if (level <= 2) return '#22c55e';
  if (level <= 4) return '#eab308';
  if (level <= 5) return '#f97316';
  return '#C8102E';
};

const getComplexityDots = (level) => {
  return Array.from({ length: 5 }, (_, i) => i < Math.ceil(level * 5 / 7));
};

const FabricScaleSection = () => {
  const [activeTab, setActiveTab] = useState('knit');
  const [activeLevel, setActiveLevel] = useState(4);
  const fabrics = activeTab === 'knit' ? KNIT_FABRICS : WOVEN_FABRICS;
  const activeFabric = fabrics.find(f => f.level === activeLevel);

  return (
    <section id="fabric-scale" data-testid="fabric-scale-section" className="relative overflow-hidden" style={{ background: '#FAF8F5', padding: '70px 0' }}>
      <div className="container-main relative z-10">
        <SectionLabel number="04" title="Material Mastery" />
        <FadeInUp>
          <AccentLine />
          <h2 className="font-display mb-3" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 700, color: '#1C1C1E' }}>
            The Fabric <span style={{ color: '#C8102E' }}>Complexity</span> Scale.
          </h2>
          <p className="font-body mb-12" style={{ fontSize: '1.05rem', color: '#6B6B6F', maxWidth: '640px' }}>
            From basic cotton jerseys to PTFE membrane composites — 7 levels of manufacturing complexity, all under one roof.
          </p>
        </FadeInUp>

        {/* Tabs */}
        <FadeInUp delay={0.1}>
          <div className="flex gap-2 mb-10">
            {['knit', 'woven'].map(tab => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); setActiveLevel(4); }}
                data-testid={`fabric-tab-${tab}`}
                className="font-mono text-[0.7rem] tracking-[0.12em] uppercase px-5 py-2.5 rounded transition-all duration-300"
                style={{
                  background: activeTab === tab ? '#1C1C1E' : '#FFFFFF',
                  color: activeTab === tab ? '#FFFFFF' : '#6B6B6F',
                  border: `1px solid ${activeTab === tab ? '#1C1C1E' : '#D4CFC7'}`,
                }}
              >
                {tab} Fabrics
              </button>
            ))}
          </div>
        </FadeInUp>

        {/* Scale */}
        <FadeInUp delay={0.2}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
            {/* Levels */}
            <div className="space-y-1">
              {fabrics.map((f) => (
                <motion.button
                  key={f.level}
                  onClick={() => setActiveLevel(f.level)}
                  data-testid={`fabric-level-${f.level}`}
                  className="w-full text-left flex items-center gap-4 p-4 rounded-lg transition-all duration-300"
                  style={{
                    background: activeLevel === f.level ? '#FFFFFF' : 'transparent',
                    border: activeLevel === f.level ? '1px solid #D4CFC7' : '1px solid transparent',
                    boxShadow: activeLevel === f.level ? '0 2px 10px rgba(0,0,0,0.04)' : 'none',
                  }}
                  whileHover={{ x: 3 }}
                >
                  <span className="font-mono text-[0.67rem]" style={{ color: '#6B6B6F', width: '28px' }}>L{f.level}</span>
                  <span className="flex-1 font-body text-sm" style={{ color: activeLevel === f.level ? '#1C1C1E' : '#6B6B6F', fontWeight: activeLevel === f.level ? 500 : 400 }}>
                    {f.name}
                  </span>
                  {/* Complexity dots */}
                  <div className="flex gap-1">
                    {getComplexityDots(f.level).map((filled, di) => (
                      <div key={di} className="w-2 h-2 rounded-full" style={{ background: filled ? '#C8102E' : '#E8E0D5' }} />
                    ))}
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Detail panel */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`${activeTab}-${activeLevel}`}
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -15 }}
                transition={{ duration: 0.25 }}
                className="card-light p-8"
                data-testid="fabric-detail-panel"
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="font-display" style={{ fontSize: '3.5rem', fontWeight: 300, color: 'rgba(200,16,46,0.1)' }}>L{activeFabric?.level}</span>
                  <span
                    className="font-mono text-[0.65rem] tracking-[0.1em] uppercase px-3 py-1 rounded"
                    style={{ background: getBarColor(activeFabric?.level), color: activeFabric?.level >= 5 ? 'white' : '#1C1C1E' }}
                  >
                    {activeFabric?.handling}
                  </span>
                </div>

                <h3 className="font-display mb-3" style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1C1C1E' }}>{activeFabric?.name}</h3>
                <p className="font-body mb-8" style={{ fontSize: '0.95rem', color: '#6B6B6F', lineHeight: 1.7 }}>{activeFabric?.desc}</p>

                <div className="grid grid-cols-2 gap-3">
                  <div style={{ background: '#FAF8F5', padding: '16px', borderRadius: '8px' }}>
                    <span className="font-mono block mb-1" style={{ fontSize: '0.6rem', color: '#6B6B6F', textTransform: 'uppercase', letterSpacing: '0.12em' }}>GSM Range</span>
                    <span className="font-display" style={{ fontSize: '1.2rem', fontWeight: 600, color: '#1C1C1E' }}>{activeFabric?.gsm}</span>
                  </div>
                  <div style={{ background: '#FAF8F5', padding: '16px', borderRadius: '8px' }}>
                    <span className="font-mono block mb-1" style={{ fontSize: '0.6rem', color: '#6B6B6F', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Complexity</span>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: '#E8E0D5' }}>
                        <motion.div
                          className="h-full rounded-full"
                          style={{ background: getBarColor(activeFabric?.level) }}
                          initial={{ width: 0 }}
                          animate={{ width: `${(activeFabric?.level / 7) * 100}%` }}
                          transition={{ duration: 0.8 }}
                        />
                      </div>
                      <span className="font-mono" style={{ fontSize: '0.7rem', color: '#6B6B6F' }}>{activeFabric?.level}/7</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </FadeInUp>

        {/* Bottom stats */}
        <FadeInUp delay={0.3}>
          <div className="flex flex-wrap gap-10 md:gap-16 mt-14 pt-6" style={{ borderTop: '1px solid #D4CFC7' }}>
            {[
              { val: '14', label: 'Fabric Categories' },
              { val: 'L1\u2013L7', label: 'Full Range' },
              { val: '30+', label: 'Years Handling' },
            ].map((s, i) => (
              <div key={i}>
                <span className="font-display block" style={{ fontSize: '1.8rem', fontWeight: 700, color: '#1C1C1E' }}>{s.val}</span>
                <span className="font-mono" style={{ fontSize: '0.67rem', color: '#6B6B6F', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{s.label}</span>
              </div>
            ))}
          </div>
        </FadeInUp>
      </div>
      <div className="section-number">04</div>
    </section>
  );
};

export default FabricScaleSection;
